/**
 * De factuurgenerator op /factuur-maken.
 *
 * Alles gebeurt in de browser. De ingevulde gegevens gaan niet naar een server en
 * worden alleen in localStorage bewaard, zodat een tweede factuur sneller gaat.
 * De pdf komt uit het afdrukvenster van de browser; de printregels in
 * resources/css/elements/generator.css zetten daarvoor alleen het
 * factuurvel op papier.
 */

const OPSLAGSLEUTEL = 'agendafactuur.factuur-maken';
const TELLERSLEUTEL = 'agendafactuur.factuur-maken.afdrukken';

const geld = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const datumNotatie = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

const form = document.querySelector('[data-factuurgenerator]');
const vel = document.querySelector('[data-factuurvel]');

if (form && vel) {
    start(form, vel);
}

function start(form, vel) {
    const regelsHolder = form.querySelector('[data-regels]');
    const velRegels = vel.querySelector('[data-vel-regels]');
    const velTotalen = vel.querySelector('[data-vel-totalen]');
    const controle = document.querySelector('[data-controle]');
    const controleLijst = document.querySelector('[data-controle-lijst]');
    const upsell = document.querySelector('[data-upsell]');

    const datumveld = form.querySelector('[name="factuur_datum"]');
    const nummerveld = form.querySelector('[name="factuur_nummer"]');

    if (datumveld && !datumveld.value) {
        datumveld.value = new Date().toISOString().slice(0, 10);
    }

    if (nummerveld && !nummerveld.value) {
        nummerveld.value = new Date().getFullYear() + '-001';
    }

    herstel(form, regelsHolder);

    form.addEventListener('input', () => {
        teken();
        bewaar(form);
    });

    form.addEventListener('change', () => {
        teken();
        bewaar(form);
    });

    form.addEventListener('click', (event) => {
        if (event.target.closest('[data-regel-erbij]')) {
            regelErbij(regelsHolder);
            teken();
            bewaar(form);
        }

        if (event.target.closest('[data-regel-weg]')) {
            regelWeg(event.target.closest('[data-regel]'), regelsHolder);
            teken();
            bewaar(form);
        }
    });

    /**
     * De browser zet de documenttitel in de kopregel van de afdruk en gebruikt hem
     * als bestandsnaam bij Opslaan als pdf. Tijdens het afdrukken staat daar dus
     * het factuurnummer in plaats van de titel van de pagina.
     */
    const paginatitel = document.title;

    window.addEventListener('beforeprint', () => {
        const nummer = nummerveld?.value.trim();
        document.title = nummer ? 'Factuur ' + nummer : 'Factuur';
    });

    window.addEventListener('afterprint', () => {
        document.title = paginatitel;
    });

    document.querySelector('[data-actie="download"]')?.addEventListener('click', () => {
        teken();
        telAfdruk(upsell);
        window.print();
    });

    document.querySelector('[data-actie="leegmaken"]')?.addEventListener('click', () => {
        maakLeeg(form, regelsHolder);
        teken();
    });

    teken();

    function teken() {
        const gegevens = lees(form, regelsHolder);

        vulTekstvelden(vel, gegevens);
        vulBetaallink(vel, gegevens);
        vulRegels(velRegels, gegevens);
        vulTotalen(velTotalen, gegevens);
        vulControle(controle, controleLijst, gegevens);

        vel.querySelectorAll('[data-vel-btwkop]').forEach((kop) => {
            kop.hidden = gegevens.kor;
        });
    }
}

/**
 * Leest het formulier uit en rekent de bedragen door.
 */
function lees(form, regelsHolder) {
    const waarde = (naam) => (form.querySelector('[name="' + naam + '"]')?.value || '').trim();
    const kor = form.querySelector('[name="factuur_kor"]')?.checked === true;

    const regels = Array.from(regelsHolder.querySelectorAll('[data-regel]')).map((regel) => {
        const veld = (naam) => regel.querySelector('[data-veld="' + naam + '"]');
        const aantal = getal(veld('aantal')?.value);
        const prijs = getal(veld('prijs')?.value);
        const btw = kor ? 0 : getal(veld('btw')?.value);

        const datum = datumUit(veld('datum')?.value);

        return {
            omschrijving: (veld('omschrijving')?.value || '').trim(),
            datum: datum ? datumNotatie.format(datum) : '',
            aantal,
            prijs,
            btw,
            bedrag: aantal * prijs,
        };
    });

    const gevuld = regels.filter((regel) => regel.omschrijving !== '' || regel.bedrag !== 0);
    const subtotaal = gevuld.reduce((som, regel) => som + regel.bedrag, 0);

    const btwPerTarief = new Map();
    gevuld.forEach((regel) => {
        if (regel.btw === 0) {
            return;
        }

        const eerder = btwPerTarief.get(regel.btw) || 0;
        btwPerTarief.set(regel.btw, eerder + (regel.bedrag * regel.btw) / 100);
    });

    const btwTotaal = Array.from(btwPerTarief.values()).reduce((som, bedrag) => som + bedrag, 0);
    const datum = datumUit(waarde('factuur_datum'));
    const termijn = parseInt(waarde('factuur_termijn'), 10) || 14;

    return {
        afzender_naam: waarde('afzender_naam'),
        afzender_adres: waarde('afzender_adres'),
        afzender_plaats: samen(waarde('afzender_postcode'), waarde('afzender_plaats')),
        afzender_kvk: waarde('afzender_kvk') ? 'KvK ' + waarde('afzender_kvk') : '',
        afzender_btw: waarde('afzender_btw') ? 'Btw-id ' + waarde('afzender_btw') : '',
        afzender_iban: waarde('afzender_iban'),
        klant_naam: waarde('klant_naam'),
        klant_attentie: waarde('klant_attentie') ? 't.a.v. ' + waarde('klant_attentie') : '',
        klant_adres: waarde('klant_adres'),
        klant_plaats: samen(waarde('klant_postcode'), waarde('klant_plaats')),
        factuur_nummer: waarde('factuur_nummer'),
        betaallink: waarde('factuur_tikkie'),
        factuur_datum: datum ? datumNotatie.format(datum) : '',
        vervaldatum: datum ? datumNotatie.format(plusDagen(datum, termijn)) : '',
        termijn,
        kor,
        opmerking: waarde('factuur_opmerking'),
        regels: gevuld,
        subtotaal,
        btwPerTarief,
        btwTotaal,
        totaal: subtotaal + btwTotaal,
    };
}

function vulTekstvelden(vel, gegevens) {
    const zetten = {
        afzender_naam: gegevens.afzender_naam || 'Je bedrijfsnaam',
        afzender_adres: gegevens.afzender_adres,
        afzender_plaats: gegevens.afzender_plaats,
        afzender_kvk: gegevens.afzender_kvk,
        afzender_btw: gegevens.afzender_btw,
        klant_naam: gegevens.klant_naam || 'Je klant',
        klant_attentie: gegevens.klant_attentie,
        klant_adres: gegevens.klant_adres,
        klant_plaats: gegevens.klant_plaats,
        factuur_nummer: gegevens.factuur_nummer,
        factuur_datum: gegevens.factuur_datum,
        betaling: betaalregel(gegevens),
        opmerking: gegevens.opmerking,
    };

    Object.entries(zetten).forEach(([naam, tekst]) => {
        const element = vel.querySelector('[data-vel="' + naam + '"]');

        if (!element) {
            return;
        }

        element.textContent = tekst;
        element.hidden = tekst === '';
    });
}

/**
 * De betaallink van Tikkie of een andere aanbieder. Alleen http en https komen erin,
 * zodat een geplakte javascript:-regel geen link wordt. Op de factuur staat de link
 * zonder protocol, want dat leest prettiger op papier.
 */
function vulBetaallink(vel, gegevens) {
    const regel = vel.querySelector('[data-vel-tikkie]');
    const anker = vel.querySelector('[data-vel-tikkie-link]');

    if (!regel || !anker) {
        return;
    }

    let adres = null;

    try {
        const ontleed = new URL(gegevens.betaallink);

        if (ontleed.protocol === 'https:' || ontleed.protocol === 'http:') {
            adres = ontleed;
        }
    } catch (fout) {
        adres = null;
    }

    regel.hidden = adres === null;

    if (adres === null) {
        anker.removeAttribute('href');
        anker.textContent = '';

        return;
    }

    anker.href = adres.href;
    anker.textContent = (adres.host + adres.pathname + adres.search).replace(/\/$/, '');
}

function betaalregel(gegevens) {
    if (!gegevens.vervaldatum) {
        return '';
    }

    const bedrag = geld.format(gegevens.totaal);

    if (gegevens.afzender_iban) {
        return 'Betaal ' + bedrag + ' voor ' + gegevens.vervaldatum + ' op ' + gegevens.afzender_iban
            + ' onder vermelding van factuurnummer ' + (gegevens.factuur_nummer || '') + '.';
    }

    return 'Betaal ' + bedrag + ' voor ' + gegevens.vervaldatum + '.';
}

function vulRegels(tbody, gegevens) {
    tbody.textContent = '';

    gegevens.regels.forEach((regel) => {
        const rij = document.createElement('tr');

        rij.appendChild(omschrijvingscel(regel));
        rij.appendChild(cel(nummer(regel.aantal), 'generator-vel__getal'));
        rij.appendChild(cel(geld.format(regel.prijs), 'generator-vel__getal'));

        if (!gegevens.kor) {
            rij.appendChild(cel(regel.btw + '%', 'generator-vel__getal'));
        }

        rij.appendChild(cel(geld.format(regel.bedrag), 'generator-vel__getal'));

        tbody.appendChild(rij);
    });

    if (gegevens.regels.length === 0) {
        const rij = document.createElement('tr');
        const leeg = cel('Vul hiernaast in wat je hebt geleverd');
        leeg.colSpan = gegevens.kor ? 4 : 5;
        leeg.className = 'generator-vel__leeg';
        rij.appendChild(leeg);
        tbody.appendChild(rij);
    }
}

function vulTotalen(lijst, gegevens) {
    lijst.textContent = '';

    if (gegevens.kor) {
        totaalregel(lijst, 'Totaal', geld.format(gegevens.subtotaal), true);
        totaalregel(lijst, 'Btw', 'Geen btw, kleineondernemersregeling van toepassing');

        return;
    }

    totaalregel(lijst, 'Subtotaal', geld.format(gegevens.subtotaal));

    Array.from(gegevens.btwPerTarief.entries())
        .sort((a, b) => b[0] - a[0])
        .forEach(([tarief, bedrag]) => {
            totaalregel(lijst, 'Btw ' + tarief + '%', geld.format(bedrag));
        });

    totaalregel(lijst, 'Totaal inclusief btw', geld.format(gegevens.totaal), true);
}

function totaalregel(lijst, naam, bedrag, zwaar = false) {
    const dt = document.createElement('dt');
    const dd = document.createElement('dd');

    dt.textContent = naam;
    dd.textContent = bedrag;

    if (zwaar) {
        dt.className = 'generator-vel__zwaar';
        dd.className = 'generator-vel__zwaar';
    }

    lijst.appendChild(dt);
    lijst.appendChild(dd);
}

/**
 * De negen onderdelen die de Belastingdienst op een factuur vraagt, plus het IBAN
 * dat wettelijk mag ontbreken maar waar je klant niet zonder kan.
 */
function vulControle(controle, lijst, gegevens) {
    if (!controle || !lijst) {
        return;
    }

    const ontbreekt = [];

    if (!gegevens.afzender_naam) {
        ontbreekt.push('je bedrijfsnaam');
    }

    if (!gegevens.afzender_adres || !gegevens.afzender_plaats) {
        ontbreekt.push('je adres');
    }

    if (!gegevens.afzender_kvk) {
        ontbreekt.push('je KvK-nummer');
    }

    if (!gegevens.afzender_btw) {
        ontbreekt.push('je btw-identificatienummer');
    }

    if (!gegevens.klant_naam) {
        ontbreekt.push('de naam van je klant');
    }

    if (!gegevens.klant_adres || !gegevens.klant_plaats) {
        ontbreekt.push('het adres van je klant');
    }

    if (!gegevens.factuur_nummer) {
        ontbreekt.push('een opeenvolgend factuurnummer');
    }

    if (!gegevens.factuur_datum) {
        ontbreekt.push('de factuurdatum');
    }

    if (gegevens.regels.length === 0) {
        ontbreekt.push('een omschrijving van wat je hebt geleverd');
    }

    if (!gegevens.afzender_iban) {
        ontbreekt.push('je IBAN, want zonder rekeningnummer kan je klant niet betalen');
    }

    lijst.textContent = '';

    ontbreekt.forEach((tekst) => {
        const item = document.createElement('li');
        item.textContent = tekst;
        lijst.appendChild(item);
    });

    controle.hidden = ontbreekt.length === 0;
}

function regelErbij(regelsHolder) {
    const laatste = regelsHolder.querySelector('[data-regel]:last-of-type');
    const kopie = laatste.cloneNode(true);
    const volgnummer = regelsHolder.querySelectorAll('[data-regel]').length + 1;

    kopie.querySelectorAll('input, select').forEach((veld) => {
        const label = kopie.querySelector('label[for="' + veld.id + '"]');
        veld.id = veld.id.replace(/\d+$/, volgnummer);

        if (label) {
            label.setAttribute('for', veld.id);
        }

        if (veld.dataset.veld === 'aantal') {
            veld.value = '1';
        } else if (veld.dataset.veld === 'btw') {
            veld.value = veld.value || '21';
        } else if (veld.tagName === 'INPUT') {
            veld.value = '';
        }
    });

    regelsHolder.appendChild(kopie);
    kopie.querySelector('[data-veld="omschrijving"]')?.focus();
}

function regelWeg(regel, regelsHolder) {
    if (regelsHolder.querySelectorAll('[data-regel]').length === 1) {
        regel.querySelectorAll('input').forEach((veld) => {
            veld.value = veld.dataset.veld === 'aantal' ? '1' : '';
        });

        return;
    }

    regel.remove();
}

function maakLeeg(form, regelsHolder) {
    form.reset();

    regelsHolder.querySelectorAll('[data-regel]').forEach((regel, index) => {
        if (index === 0) {
            regel.querySelectorAll('input').forEach((veld) => {
                veld.value = veld.dataset.veld === 'aantal' ? '1' : '';
            });

            return;
        }

        regel.remove();
    });

    form.querySelector('[name="factuur_datum"]').value = new Date().toISOString().slice(0, 10);
    form.querySelector('[name="factuur_nummer"]').value = new Date().getFullYear() + '-001';

    vergeet();
}

function bewaar(form) {
    const gegevens = {
        velden: {},
        regels: [],
    };

    form.querySelectorAll('[name]').forEach((veld) => {
        gegevens.velden[veld.name] = veld.type === 'checkbox' ? veld.checked : veld.value;
    });

    form.querySelectorAll('[data-regel]').forEach((regel) => {
        const veld = (naam) => regel.querySelector('[data-veld="' + naam + '"]')?.value || '';

        gegevens.regels.push({
            omschrijving: veld('omschrijving'),
            datum: veld('datum'),
            aantal: veld('aantal'),
            prijs: veld('prijs'),
            btw: veld('btw'),
        });
    });

    try {
        window.localStorage.setItem(OPSLAGSLEUTEL, JSON.stringify(gegevens));
    } catch (fout) {
        // Een browser in privémodus of met geblokkeerde opslag; de generator werkt dan
        // gewoon door, alleen onthoudt hij niets voor de volgende keer.
    }
}

function herstel(form, regelsHolder) {
    let gegevens;

    try {
        gegevens = JSON.parse(window.localStorage.getItem(OPSLAGSLEUTEL) || 'null');
    } catch (fout) {
        return;
    }

    if (!gegevens) {
        return;
    }

    Object.entries(gegevens.velden || {}).forEach(([naam, waarde]) => {
        const veld = form.querySelector('[name="' + naam + '"]');

        if (!veld) {
            return;
        }

        if (veld.type === 'checkbox') {
            veld.checked = waarde === true;
        } else {
            veld.value = waarde;
        }
    });

    (gegevens.regels || []).forEach((regel, index) => {
        if (index > 0) {
            regelErbij(regelsHolder);
        }

        const element = regelsHolder.querySelectorAll('[data-regel]')[index];

        if (!element) {
            return;
        }

        Object.entries(regel).forEach(([naam, waarde]) => {
            const veld = element.querySelector('[data-veld="' + naam + '"]');

            if (veld) {
                veld.value = waarde;
            }
        });
    });
}

function vergeet() {
    try {
        window.localStorage.removeItem(OPSLAGSLEUTEL);
    } catch (fout) {
        // Zie bewaar().
    }
}

/**
 * Na de eerste afdruk laten we zien wat een account erbij doet. Wie hier voor het
 * eerst komt, krijgt eerst zijn factuur en pas daarna het aanbod.
 */
function telAfdruk(upsell) {
    let aantal = 0;

    try {
        aantal = parseInt(window.localStorage.getItem(TELLERSLEUTEL) || '0', 10) + 1;
        window.localStorage.setItem(TELLERSLEUTEL, String(aantal));
    } catch (fout) {
        aantal = 1;
    }

    if (upsell && aantal >= 1) {
        upsell.hidden = false;
    }
}

/**
 * De omschrijving met daaronder de datum van de afspraak. De Belastingdienst wil
 * bij diensten de datum of de periode van levering zien, en per regel is dat
 * nauwkeuriger dan één datum voor de hele factuur.
 */
function omschrijvingscel(regel) {
    const td = cel(regel.omschrijving);

    if (regel.datum) {
        const datum = document.createElement('span');
        datum.className = 'generator-vel__regeldatum';
        datum.textContent = regel.datum;
        td.appendChild(datum);
    }

    return td;
}

function cel(tekst, klasse = '') {
    const td = document.createElement('td');
    td.textContent = tekst;

    if (klasse) {
        td.className = klasse;
    }

    return td;
}

/**
 * Postcode en plaats staan in twee velden en horen op de factuur op één regel.
 */
function samen(postcode, plaats) {
    return [postcode, plaats].filter((deel) => deel !== '').join('  ');
}

function getal(waarde) {
    const schoon = String(waarde ?? '').replace(',', '.').trim();
    const uitkomst = Number.parseFloat(schoon);

    return Number.isFinite(uitkomst) ? uitkomst : 0;
}

function nummer(waarde) {
    return Number.isInteger(waarde) ? String(waarde) : waarde.toLocaleString('nl-NL');
}

function datumUit(waarde) {
    if (!waarde) {
        return null;
    }

    const datum = new Date(waarde + 'T00:00:00');

    return Number.isNaN(datum.getTime()) ? null : datum;
}

function plusDagen(datum, dagen) {
    const nieuw = new Date(datum.getTime());
    nieuw.setDate(nieuw.getDate() + dagen);

    return nieuw;
}
